function cartDrawerSectionId() {
  const section = document.getElementById('CartDrawer')?.closest('.shopify-section');
  if (!section?.id) return 'cart-drawer';
  return section.id.replace('shopify-section-', '');
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
      if (!summary.hasAttribute('role')) this.setSummaryAccessibility(summary);
    });

    document.body.classList.add('overflow-hidden');

    const startSlide = () => {
      this.classList.add('active');
      this.prepareImpressionKit().then(() => {
        this.injectCartAddonTemplate();
      });
    };

    if (this.classList.contains('animate')) {
      startSlide();
    } else {
      this.classList.add('animate', 'is-opening');
      void this.offsetWidth;
      requestAnimationFrame(() => {
        startSlide();
        this.classList.remove('is-opening');
      });
    }

    const drawerInner = this.querySelector('.drawer__inner');
    const onOpened = (event) => {
      if (event.target !== drawerInner) return;
      if (event.propertyName && event.propertyName !== 'transform') return;

      drawerInner.removeEventListener('transitionend', onOpened);

      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = drawerInner || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    };

    drawerInner?.addEventListener('transitionend', onOpened);
  }

  setValidating(isValidating) {
    this.classList.toggle('cart-drawer--validating', isValidating);
    if (isValidating) this.ensureValidatingOverlay();
  }

  ensureValidatingOverlay() {
    if (!this.classList.contains('cart-drawer--validating')) return;

    const inner = this.querySelector('.drawer__inner');
    if (!inner || inner.querySelector(':scope > .cart-drawer__validating')) return;

    const overlay = document.createElement('div');
    overlay.className = 'cart-drawer__validating';
    overlay.setAttribute('aria-live', 'polite');
    overlay.innerHTML = '<span class="cart-drawer__validating-spinner"></span>';
    inner.prepend(overlay);
  }

  prepareImpressionKit() {
    const finish = (kit) => {
      if (!kit?.ensureReady || kit.handled) {
        this.setValidating(false);
        return Promise.resolve();
      }

      this.classList.add('cart-drawer--validating');
      this.ensureValidatingOverlay();
      return kit
        .ensureReady()
        .then((result) => {
          if (!result?.changed) return;
          return this.querySelector('cart-drawer-items')?.onCartUpdate?.();
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => this.setValidating(false));
    };

    const kit = document.querySelector('cart-impression-kit');
    if (kit?.ensureReady) return finish(kit);

    if (kit && !customElements.get('cart-impression-kit')) {
      return customElements
        .whenDefined('cart-impression-kit')
        .then(() => finish(document.querySelector('cart-impression-kit')))
        .catch(() => this.setValidating(false));
    }

    this.setValidating(false);
    return Promise.resolve();
  }

  close() {
    this.classList.remove('active', 'is-opening');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  injectCartAddonTemplate() {
    const template = this.querySelector('#cart-addon-template');
    const target = this.querySelector('#CartDrawer-CartItems');
    if (!template || !target) return;

    const existingAddon = this.querySelector('.cart-addon');
    if (existingAddon) existingAddon.remove();

    const fragment = document.createElement('div');
    fragment.innerHTML = template.innerHTML.trim();
    while (fragment.firstChild) {
      target.appendChild(fragment.firstChild);
    }
  }

  renderContents(parsedState, options = {}) {
    this.setValidating(true);

    if (!parsedState?.sections) {
      this.setValidating(false);
      return;
    }

    const drawerSectionId = cartDrawerSectionId();
    if (window.CartSorter && parsedState.items?.length && parsedState.sections?.[drawerSectionId]) {
      parsedState.sections[drawerSectionId] = CartSorter.sortSectionHtml(
        parsedState.sections[drawerSectionId],
        parsedState.items,
        'drawer'
      );
    }

    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.querySelector('cart-drawer-items')?.classList.add('cart-items--sorted');
    this.ensureValidatingOverlay();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      if (options.errorMessage && typeof CartItems !== 'undefined') {
        CartItems.showCartError(options.errorMessage);
      }
      if (options.open === false) {
        this.setValidating(false);
        return;
      }
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: cartDrawerSectionId(),
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  onCartUpdate() {
    return super.onCartUpdate().then(() => {
      const drawer = this.closest('cart-drawer');
      drawer?.ensureValidatingOverlay?.();
      drawer?.injectCartAddonTemplate();
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: cartDrawerSectionId(),
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
